// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardButton from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardButton/ExamCardButton";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";
import ExamCardModal from "src/components/shared/modals/ExamCardModal/ExamCardModal";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import { SelectChangeEvent } from "@mui/material/Select";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";

// Types
import {
  AddEditDryEyeType,
  DryEyeProfileType,
} from "src/services/clinics/types";

// Utils
import { DRY_EYE_FIELDS } from "src/utils/exams/lensSimulationHelper";

const DEFAULT_DRY_EYE_VALUES: Record<string, Record<string, string>> = {
  Mild: {
    blurType: "Mild",
    glaresType: "Mild",
    halosType: "Mild",
    reducedContrastType: "Mild",
    tearBreakUpTimeType: "Mild",
  },
  Moderate: {
    blurType: "Moderate",
    glaresType: "Moderate",
    halosType: "Moderate",
    reducedContrastType: "Moderate",
    tearBreakUpTimeType: "Moderate",
  },
  Severe: {
    blurType: "Severe",
    glaresType: "Severe",
    halosType: "Severe",
    reducedContrastType: "Severe",
    tearBreakUpTimeType: "Severe",
  },
};

type AddEditDryEyeModalType = {
  open: boolean;
  title: string;
  onClose: () => void;
  onConfirm: (lensData: AddEditDryEyeType) => void;
  isUpdating: boolean;
  profileToEdit?: DryEyeProfileType | undefined;
};

const AddEditDryEyeModal = ({
  open,
  title,
  onClose,
  onConfirm,
  isUpdating,
  profileToEdit,
}: AddEditDryEyeModalType) => {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [severity, setSeverity] = useState({ displayName: "", value: "" });
  const [treated, setTreated] = useState(false);
  const [enumValues, setEnumValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(DRY_EYE_FIELDS.map((field) => [field.parameterName, ""]))
  );

  const formattedSeverities = useMemo(
    () => [
      { displayName: t("Mild"), value: "Mild" },
      { displayName: t("Moderate"), value: "Moderate" },
      { displayName: t("Severe"), value: "Severe" },
    ],
    [t]
  );

  useEffect(() => {
    if (profileToEdit && open) {
      setName(profileToEdit.name);
      setPrice(profileToEdit.price ?? "");
      setTreated(profileToEdit.treated);
      setSeverity({
        displayName: t(profileToEdit.severityType),
        value: profileToEdit.severityType,
      });

      setEnumValues(
        Object.fromEntries(
          DRY_EYE_FIELDS.map((field) => {
            const currentValue = profileToEdit[field.parameterName];
            const isValid = (field.options as readonly string[]).includes(
              currentValue
            );
            return [field.parameterName, isValid ? currentValue : ""];
          })
        )
      );
    } else if (!profileToEdit && open) {
      setTreated(false);
      setEnumValues(
        Object.fromEntries(
          DRY_EYE_FIELDS.map((field) => [field.parameterName, field.options[0]])
        )
      );
    }
  }, [profileToEdit, open, t]);

  const handleSetSeverity = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = formattedSeverities?.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        setSeverity({ ...value });

        // set default values when first selecting a severity
        const defaultEnumValues = DEFAULT_DRY_EYE_VALUES[value.value];
        if (defaultEnumValues) {
          setEnumValues(defaultEnumValues);
          setTreated(false);
        }
      }
    },
    [formattedSeverities]
  );

  const shouldDisable = useMemo(() => !name, [name]);

  const resetValues = useCallback(() => {
    setName("");
    setPrice("");
    setSeverity({ displayName: "", value: "" });
    setTreated(false);
    setEnumValues(
      Object.fromEntries(
        DRY_EYE_FIELDS.map((field) => [field.parameterName, ""])
      )
    );
  }, []);

  const handleClose = useCallback(() => {
    resetValues();
    onClose();
  }, [resetValues, onClose]);

  const handleConfirm = useCallback(() => {
    const profileData = {
      name,
      price,
      severityType: severity.value,
      treated,
      ...Object.fromEntries(
        DRY_EYE_FIELDS.map((field) => [
          field.parameterName,
          enumValues[field.parameterName],
        ])
      ),
    } as AddEditDryEyeType;
    resetValues();
    onConfirm(profileData);
  }, [
    name,
    price,
    severity.value,
    treated,
    enumValues,
    resetValues,
    onConfirm,
  ]);

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  const tooltipStyle = {
    color: "#FFF",
    fontSize: "12px",
    width: "16px",
  };

  return (
    <ExamCardModal
      backgroundColor="#202338"
      isOpen={open}
      title={title}
      onClose={handleClose}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            data-testid="button-save"
            disabled={shouldDisable}
            sx={{
              ...ModalCustomConfirm(),
              "&.Mui-disabled": {
                color: "#FFF",
                backgroundColor: "#435B6D7A",
              },
            }}
            onClick={handleConfirm}
          >
            {t("Save")}
          </Button>
        </Grid>
      }
    >
      <Grid
        display="flex"
        flexDirection="column"
        p="8px"
        gap="8px"
        sx={{ backgroundColor: "#202338" }}
      >
        {/* Row 1: Name + Severity Type */}
        <Box display="flex" gap="8px">
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography color="#FFF" fontSize="12px">
              {t("Name")}*
            </Typography>
            <TextInputField
              id="iol-name-input"
              required
              disabled={isUpdating}
              autoFocus={!profileToEdit}
              placeholder={t("Enter a value")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <TitleWithTooltip
              title={`${t("Severity")}*`}
              tooltip={
                <div>{t("Overall severity of the dry eye condition.")}</div>
              }
              titleStyle={textStyle}
              tooltipStyle={tooltipStyle}
            />
            <SelectDropdown
              id="severity-dropdown"
              disabled={isUpdating}
              dropdownOptions={formattedSeverities}
              value={severity.displayName}
              handleChange={handleSetSeverity}
            />
          </Box>
        </Box>

        {/* Row 2: Price */}
        <Box display="flex" gap="8px">
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography color="#FFF" fontSize="12px">
              {t("Price")}
            </Typography>
            <TextInputField
              id="iol-price-input"
              required
              disabled={isUpdating}
              placeholder={t("Enter a value")}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </Box>
        </Box>

        {/* Severity fields: Halos, Blur, Glare, Reduced Contrast --
            1 per line. */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {DRY_EYE_FIELDS.map(({ parameterName, title, tooltip, options }) => (
            <Box
              key={parameterName}
              display="flex"
              flexDirection="column"
              gap="4px"
            >
              <TitleWithTooltip
                title={`${t(title)}*`}
                tooltip={<div>{t(tooltip)}</div>}
                titleStyle={textStyle}
                tooltipStyle={tooltipStyle}
              />
              <Box display="flex" gap="4px">
                {options.map((option) => (
                  <ExamCardButton
                    key={`${parameterName}-${option}`}
                    label={t(option)}
                    isSelected={enumValues[parameterName] === option}
                    updateValue={() =>
                      setEnumValues((prev) => ({
                        ...prev,
                        [parameterName]: option,
                      }))
                    }
                    isDisabled={isUpdating}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gap="4px"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Box display="flex" alignItems="center" gap="8px">
            <CustomCheckbox
              id="treated-checkbox"
              checked={treated}
              onClick={() => {
                setTreated((prev) => !prev);
              }}
              disabled={isUpdating}
              checkedFill="#E2772E"
              checkedStroke="#FFF"
              uncheckedFill="#FFF"
              uncheckedStroke="#5B6D7A"
            />
            <Typography color="#FFF" fontSize="12px">
              {t("Treated")}
            </Typography>
          </Box>
        </Box>
      </Grid>
    </ExamCardModal>
  );
};

export default AddEditDryEyeModal;
